/**
 * TutorialBanner · 分类新手教程入口 banner（对话滚动区顶部）
 *
 * 展示规则 = (① 或 ②) 且 当天未被该用户关闭（判定纯函数 shouldShowTutorialBanner，
 * 用户维度单条目 localStorage 缓存说明见 utils/classifyTutorial.ts）：
 *  ① 当前用户没批量创建过文件夹（用户级标记，批量建夹确认成功时写入）
 *  ② 团队文件夹用量为 0（服务端接口4 folderQuotaSlice.used）
 *
 * 布局：渲染在 ChatFeed 滚动容器内部首位 + position: sticky 浮动固定——
 * 初始占文档流顶部（第一屏消息不被遮挡），滚动时钉在滚动区顶边、消息从
 * 下方穿过，下边缘 10px 白色渐变（0.6→0）柔化穿过的内容（样式见 scss）。
 *
 * 点击整卡 → 跳教程（URL 占位，见 CLASSIFY_TUTORIAL_URL）；点 X → 当天不再展示。
 */

import React, { useEffect, useMemo, useState } from 'react'
import { CloseOutlined, ReadOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  fetchFolderQuota,
  selectFolderQuota
} from '../../../../slices/folderQuotaSlice'
import {
  dismissTutorialToday,
  hasBatchCreatedFolders,
  isTutorialDismissedToday,
  openClassifyTutorial,
  shouldShowTutorialBanner
} from '../../utils/classifyTutorial'
import styles from './TutorialBanner.module.scss'

const TutorialBanner: React.FC = () => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s) => s.group.groupId)
  const userID = useAppSelector((s) => s.main.user?.user_id)
  const quota = useAppSelector(selectFolderQuota)

  // 关闭态放 state：点 X 立即隐藏；初值/切用户时从关闭层缓存回读
  const [dismissedToday, setDismissedToday] = useState(false)
  useEffect(() => {
    setDismissedToday(isTutorialDismissedToday(userID))
  }, [userID])

  // 批量建夹标记按用户回读一次 · 会话中途批量建夹后不实时收 banner，下次进入生效
  const batchCreated = useMemo(() => hasBatchCreatedFolders(userID), [userID])

  // 确保配额已就绪（条件②的数据源）· 与 useFolderCreateGate 相同的去重拉取模式：
  // loading 中不重复发；已 loaded 且属于当前团队则复用
  useEffect(() => {
    if (!groupID) return
    if (quota.loading) return
    if (quota.loaded && quota.groupID === String(groupID)) return
    dispatch(fetchFolderQuota({ groupID: String(groupID) }))
  }, [groupID, quota.loading, quota.loaded, quota.groupID, dispatch])

  const visible = shouldShowTutorialBanner({
    batchCreated,
    quotaLoaded: quota.loaded && quota.groupID === String(groupID || ''),
    quotaError: quota.error,
    quotaUsed: quota.used,
    dismissedToday
  })
  if (!visible) return null

  const handleDismiss = (): void => {
    dismissTutorialToday(userID)
    setDismissedToday(true)
  }

  return (
    <div className={styles.banner}>
      <button
        type="button"
        className={styles.main}
        onClick={openClassifyTutorial}
        aria-label="打开分类新手教程"
      >
        <span className={styles.iconBox} aria-hidden="true">
          <ReadOutlined />
        </span>
        <span className={styles.texts}>
          <span className={styles.title}>新手教程 · 3 步学会分类</span>
          <span className={styles.subtitle}>图文详解，跟着做就会</span>
        </span>
        <span className={styles.cta}>看教程 ›</span>
      </button>
      <button
        type="button"
        className={styles.close}
        onClick={handleDismiss}
        aria-label="关闭教程入口，今日不再展示"
      >
        <CloseOutlined />
      </button>
    </div>
  )
}

export default TutorialBanner
